import * as React from 'react';
import {translate, InjectedTranslateProps, TranslationFunction} from 'react-i18next';
import {reportSecondaryCtaClick} from '../../utils/bi';
import * as s from './Features.scss';
import Carousel from 'nuka-carousel';
import LazyLoad from 'react-lazyload';
import {features as featuresLink} from '../../utils/ctaLinks';
import NoScript from '../NoScript';
import Text from '../Text';

type FeatureImage = {
  key: string,
  img: string,
  localizationKey: string,
};

export interface FeaturesConfig {
  staticsBaseUrl: string;
  featureImages: FeatureImage[];
  key: string;
}

export interface FeaturesProps extends InjectedTranslateProps, FeaturesConfig {
}

interface FeaturesState {
  style: 'list' | 'carousel';
  slideIndex: number;
}

const reportSeeAllFeaturesClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
  reportSecondaryCtaClick(e.currentTarget);
};

const Arrow = () => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16.5 23">
      <path d="M2279.54,4614l8.46,9.5-8.46,9.5" transform="translate(-2274.56 -4612)"/>
    </svg>
  );
};

const getAssetUrl = (filePath, baseUrl) => {
  return `${baseUrl}assets/images/${filePath}`;
};

interface FeatureProps {
  t: TranslationFunction;
  staticsBaseUrl: string;
  img: string;
  localizationKey: string;
  key: string;
  supertitle: string;
}

const getFeature = (props: FeatureProps) => {
  const t = props.t;
  const url = getAssetUrl(`features/${props.img}`, props.staticsBaseUrl);
  const alt = `${props.supertitle} - ${t(`features.${props.localizationKey}.title`)} ${t('seo.features.alt.ending')}`;
  const featureClass = `feature${props.key[0].toUpperCase() + props.key.substr(1)}`;

  return (
    <div className={`${s.feature} ${s[featureClass]}`} key={props.key} data-hook={`feature-${props.key}`}>
      <div className={s.featureMedia}>
        <NoScript>
          <img src={url} alt={alt}/>
        </NoScript>
        <LazyLoad height={350} once>
          <img src={url} alt={alt}/>
        </LazyLoad>
      </div>
      <div className={s.featureBody}>
        <h3>{t(`features.${props.localizationKey}.title`)}</h3>
        <Text i18nKey={`features.${props.localizationKey}.text`} parent="p" className={s.featureText} />
      </div>
    </div>
  );
};

const getFeatures = ({t, staticsBaseUrl, supertitle, featureImages}: { t: TranslationFunction, staticsBaseUrl: string, supertitle: string, featureImages: FeatureImage[] }) => (featureImages.map((feature, i) => Object.assign({}, feature, {
  t,
  staticsBaseUrl,
  supertitle
})).map(getFeature));

const CAROUSEL_BREAKPOINT = 1280;

const ArrowNext = () => {
  return (
    <svg xmlns="https://www.w3.org/2000/svg" viewBox="0 0 68 26" width="62" height="26">
      <line x1="0" y1="12" x2="67" y2="12" style={{strokeWidth: 1, stroke: '#000'}}/>
      <path stroke="#000" strokeWidth="1" fillRule="evenodd" fill="none" d="M117.249,47.987l13.741,13-13.741,13"
            transform="translate(-63 -49)"/>
    </svg>
  );
};

const ArrowBack = () => {
  return (
    <svg xmlns="https://www.w3.org/2000/svg" viewBox="0 0 68 26" width="62" height="26">
      <line x1="1" y1="12" x2="68" y2="12" style={{strokeWidth: 1, stroke: '#000'}}/>
      <path stroke="#000" strokeWidth="1" fillRule="evenodd" fill="none" d="M76.75,74.01l-13.74-13,13.742-13"
            transform="translate(-63 -49)"/>
    </svg>
  );
};

interface PrevNextButtonProps {
  next: boolean;
  onClick?: () => void;
}

const PrevNextButton: React.SFC<PrevNextButtonProps> = ({next, onClick}) => {
  return (
    <button onClick={onClick} className={next ? s.arrowNext : s.arrowPrevious}>{next ? <ArrowNext/> :
      <ArrowBack/>}</button>
  );
};

const renderCarouselPreviousControl = ({previousSlide}) => {
  return <PrevNextButton next={false} onClick={previousSlide}/>;
};

const renderCarouselNextControl = ({nextSlide}) => {
  return <PrevNextButton next={true} onClick={nextSlide}/>;
};

class Features extends React.PureComponent<FeaturesProps, FeaturesState> {
  protected resizeTimeout;

  constructor(props: FeaturesProps) {
    super(props);
    this.state = {
      style: 'list',
      slideIndex: 0
    };
  }

  componentDidMount() {
    if (window.innerWidth < CAROUSEL_BREAKPOINT) {
      this.setState({style: 'carousel'});
    }

    window.addEventListener('resize', this.onWindowResize);
  }

  componentWillUnmount() {
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }

    window.removeEventListener('resize', this.onWindowResize);
  }

  onWindowResize = () => {
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    this.resizeTimeout = setTimeout(this.handleResize, 300);
  }

  handleResize = () => {
    const {style} = this.state;
    if (window.innerWidth < CAROUSEL_BREAKPOINT && style !== 'carousel') {
      this.setState({style: 'carousel'});
    } else if (window.innerWidth >= CAROUSEL_BREAKPOINT && style !== 'list') {
      this.setState({style: 'list'});
    }
  }

  handleAfterSlide = (slideIndex: number) => {
    this.setState({slideIndex});
  }

  handleFeatureControlClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    const slideIndex = parseInt(e.currentTarget.getAttribute('data-slideindex'), 10);
    this.setState({slideIndex});
  }

  renderFeatureControl = (feature: JSX.Element, i: number) => {
    const {slideIndex} = this.state;
    return (
      <li data-hook={`feature-control-${i}`} key={i} className={slideIndex === i ? s.activeControl : null}>
        <button data-slideindex={i} onClick={this.handleFeatureControlClick}>{i + 1}</button>
      </li>
    );
  }

  render() {
    const {t, staticsBaseUrl, featureImages} = this.props;
    const {style, slideIndex} = this.state;
    const features = getFeatures({t, staticsBaseUrl, supertitle: t('features.title'), featureImages});

    return (
      <div
          className={`page-fold ${s.features}`}
          data-hook="features-fold"
          data-foldname="features"
          data-foldnum="4"
      >
        <Text parent="h2" i18nKey="features.title" className={s.title} />
        <div className={s.featuresList} data-activefeatureindex={slideIndex}>
          {
            style === 'carousel' ?
              <Carousel
                heightMode="max"
                wrapAround={true}
                dragging={false}
                initialSlideWidth={window.innerWidth}
                renderBottomCenterControls={null}
                renderCenterLeftControls={renderCarouselPreviousControl}
                renderCenterRightControls={renderCarouselNextControl}
                slideIndex={slideIndex}
                afterSlide={this.handleAfterSlide}
              >
                {features}
              </Carousel> :
              features
          }
          <ul className={s.controls}>
            {features.map(this.renderFeatureControl)}
          </ul>
          <div className={s.moreFeatures}>
            <a
              className={s.linkSeeAll}
              data-hook="features-see-all"
              data-foldname="features"
              href={featuresLink()}
              data-message="see all features"
              onClick={reportSeeAllFeaturesClick}
            >
              <span>{t('features.seeAll')}</span><Arrow/><Arrow/>
            </a>
          </div>
        </div>
      </div>
    );
  }
}

export default translate()(Features);
